/**
 * Transient "a table resync is in progress" flag, PER TABLE.
 *
 * Set by ConnectionManager whenever it re-subscribes / reconnects to re-hydrate
 * an idle or stuck table (the felt-staleness gate). While it's active, the
 * seat-rotation layer (SeatRotationContext) treats the incoming fresh snapshot as
 * a SILENT background update instead of a fresh table load: it ignores the
 * transient POV churn a resync produces (the player is momentarily unmatched —
 * seatId -1 → spectator baseline — before the seated snapshot lands) and snaps any
 * real change, so the felt never visibly "reloads" (players rotating away and back
 * to their seats). The player is already looking at this table; the resync must
 * happen in the background.
 *
 * WHY PER TABLE. This used to be one module-wide window, which was harmless
 * while only two callers set it and both were genuinely client-wide (a reconnect
 * and the foreground re-subscribe both re-hydrate everything). EN-10628 added
 * two single-table callers - the action-recovery re-assert and the feed
 * watchdog's repair - and a single-table repair then suppressed animation on
 * EVERY open table, because `tableSnapState.isTableSnapping` consults this for
 * whichever table is asking. Measured: one action on a merely SLOW connection
 * re-asserts its table roughly every 4.5s, each arming 6s, so the window stayed
 * continuously open for the whole turn - during which every felt animation on
 * every table snapped and the arrival-gated sounds stayed silent. That is the
 * "table switching is not smooth" and "state catches up late" QA saw.
 *
 * Read imperatively (no React re-render / context plumbing) at the single seat-
 * rotation decision point. Time-boxed so it always self-clears well after the
 * resync snapshot burst has landed, even if no snapshot ever arrives.
 */

/** Per-table window expiries, keyed by String(gameID). */
const untilByTable = new Map<string, number>();

/**
 * Expiry for a window that covers EVERY table, for the events that really do
 * re-hydrate all of them at once. Kept separate rather than fanned out over the
 * open tables so those callers need not know the subscription list, and so a
 * table that hydrates during the window is covered too.
 */
let allTablesUntilMs = 0;

// Comfortably covers the gap between a re-subscribe/reconnect and the fresh
// snapshot burst landing (reconnect adds a ~500ms teardown + connect handshake
// before the server pushes), while staying short enough that a genuine seat
// rotation moments later still animates normally.
const RESYNC_WINDOW_MS = 6000;

/** Begin (or extend) the window for ONE table. */
export function markTableResyncing(
  gameID: string | number,
  windowMs: number = RESYNC_WINDOW_MS
): void {
  const key = String(gameID);
  if (!key) return;
  const until = Date.now() + windowMs;
  if (until > (untilByTable.get(key) ?? 0)) untilByTable.set(key, until);
}

/**
 * Begin (or extend) a window covering every table. For the client-wide
 * re-hydrations only: a reconnect, and the foreground re-subscribe.
 */
export function markAllTablesResyncing(windowMs: number = RESYNC_WINDOW_MS): void {
  const until = Date.now() + windowMs;
  if (until > allTablesUntilMs) allTablesUntilMs = until;
}

/**
 * True while a resync is still expected to be delivering snapshots for this
 * table. Called with no table, it answers for the client-wide window only -
 * which is the honest answer for a caller that does not know which table it is
 * asking about, since a single table's repair says nothing about any other.
 */
export function isTableResyncing(gameID?: string | number | null): boolean {
  const now = Date.now();
  if (now < allTablesUntilMs) return true;
  if (gameID === undefined || gameID === null || gameID === '') return false;
  const key = String(gameID);
  const until = untilByTable.get(key);
  if (until === undefined) return false;
  if (now >= until) {
    // Expired - drop it so the map cannot grow without bound across a long
    // session of table changes.
    untilByTable.delete(key);
    return false;
  }
  return true;
}

/** Test-only reset of the module-level windows. */
export function __resetTableResyncing(): void {
  untilByTable.clear();
  allTablesUntilMs = 0;
}
